// Only campaign labels and known public paths are accepted. No client names,
// case references, email addresses, click IDs or arbitrary query strings.
const KEY = 'billable_growth_touch_v1';
const QUEUE = 'billable_growth_events_v1';
const TTL = 30 * 24 * 60 * 60 * 1000;
const PATHS = new Set(['/landing', '/', '/life-care-planners/', '/legal-nurse-consultants/', '/resources/', '/resources/time-tracking-template/', '/resources/billable-hours-to-invoice/', '/tools/billable-hours-calculator/']);
const EVENTS = new Set(['signup_started', 'cta_clicked', 'template_download', 'template_example_download', 'calculator_used']);
// `r` is a RhettCRM campaign recipient token (an opaque id, no PII), so a
// landing view or CTA click can be tied to the person the email went to.
const KEYS = ['utm_source', 'utm_medium', 'utm_campaign', 'utm_content', 'utm_term', 'r'] as const;
export type GrowthTouch = { landing_path: string; captured_at: string } & Partial<Record<typeof KEYS[number], string>>;
export function parseTouch(raw: unknown, now: number): GrowthTouch | null {
  if (!raw || typeof raw !== 'object') return null;
  const value = raw as Record<string, unknown>;
  if (typeof value.landing_path !== 'string' || !PATHS.has(value.landing_path) || typeof value.captured_at !== 'string') return null;
  const age = now - Date.parse(value.captured_at);
  if (!Number.isFinite(age) || age < 0 || age > TTL) return null;
  const touch: GrowthTouch = { landing_path: value.landing_path, captured_at: value.captured_at };
  for (const key of KEYS) if (typeof value[key] === 'string' && /^[a-zA-Z0-9_-]{1,80}$/.test(value[key])) touch[key] = value[key];
  return touch;
}
export function captureGrowthTouch() {
  try {
    if (!PATHS.has(window.location.pathname)) return;
    const params = new URLSearchParams(window.location.search);
    const campaign = Object.fromEntries(KEYS.flatMap(key => {
      const value = params.get(key);
      return value && /^[a-zA-Z0-9_-]{1,80}$/.test(value) ? [[key, value]] : [];
    }));
    const existing = parseTouch(JSON.parse(localStorage.getItem(KEY) || 'null'), Date.now());
    if (!existing || Object.keys(campaign).length) localStorage.setItem(KEY, JSON.stringify({ ...campaign, landing_path: window.location.pathname, captured_at: new Date().toISOString() }));
  } catch { /* private browsing and storage failures must not break the app */ }
}
export function readGrowthTouch(): GrowthTouch | null {
  try { return parseTouch(JSON.parse(localStorage.getItem(KEY) || 'null'), Date.now()); } catch { return null; }
}
/** A queued event. `cta_clicked` entries (and static-page `signup_started` ones) also carry the CTA. */
export type GrowthEvent = { event: string; path: string; occurred_at: string; cta?: string; location?: string; destination?: string };
const SAFE_ID = /^[a-z0-9_-]{1,60}$/;
const SAFE_PATH = /^\/[a-zA-Z0-9_\-/]{0,80}$/;
function ctaFields(entry: Record<string, unknown>) {
  const out: Pick<GrowthEvent, 'cta' | 'location' | 'destination'> = {};
  if (typeof entry.cta === 'string' && SAFE_ID.test(entry.cta)) out.cta = entry.cta;
  if (typeof entry.location === 'string' && SAFE_ID.test(entry.location)) out.location = entry.location;
  if (typeof entry.destination === 'string' && SAFE_PATH.test(entry.destination)) out.destination = entry.destination;
  return out;
}
/**
 * Queue a sign-up CTA click. Every CTA navigates away (a full page load to
 * /sign-up), so a capture made here would race the unload; the queue is
 * replayed as `cta_clicked` on the next app pageview, timestamped at the click.
 * Same queue the static marketing pages write (marketing/browser.mjs).
 */
export function recordCtaClick(cta: string, location: string, destination = '/sign-up') {
  try {
    const raw: unknown = JSON.parse(localStorage.getItem(QUEUE) || '[]');
    const events = Array.isArray(raw) ? raw : [];
    events.push({ event: 'cta_clicked', path: window.location.pathname, occurred_at: new Date().toISOString(), cta, location, destination });
    localStorage.setItem(QUEUE, JSON.stringify(events.slice(-10)));
  } catch { /* best effort */ }
}
export function consumeGrowthEvents(): GrowthEvent[] {
  try {
    const value: unknown = JSON.parse(localStorage.getItem(QUEUE) || '[]');
    if (!Array.isArray(value)) return [];
    const events = value.filter((entry): entry is GrowthEvent => {
      if (!entry || typeof entry !== 'object' || !EVENTS.has(entry.event) || typeof entry.path !== 'string') return false;
      // SPA CTAs live on any public page (/landing, /download, /mobile); the
      // static pages' own events stay limited to their known paths.
      if (entry.event === 'cta_clicked' ? !SAFE_PATH.test(entry.path) || !SAFE_ID.test(entry.cta) : !PATHS.has(entry.path)) return false;
      const age = Date.now() - Date.parse(entry.occurred_at);
      return Number.isFinite(age) && age >= 0 && age <= TTL;
    }).slice(-10).map(({ event, path, occurred_at, ...rest }) => ({ event, path, occurred_at, ...ctaFields(rest) }));
    localStorage.removeItem(QUEUE);
    return events;
  } catch { return []; }
}
